import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { formatFileSize, formatDate, getFileIcon, isAudioFile, isVideoFile, isCsvFile } from '../utils/helpers';
import AudioPlayer from './AudioPlayer';
import apiService from '../services/apiService';

// Create global registry of all active players for multiple file management
const playerRegistry = {
  activePlayers: new Map(), // Change to Map to store both ID and component reference
  
  register(id, component) {
    this.activePlayers.set(id, component);
    console.log(`Registered player: ${id}, Active players: ${this.activePlayers.size}`);
  },
  
  unregister(id) {
    this.activePlayers.delete(id);
    console.log(`Unregistered player: ${id}, Active players: ${this.activePlayers.size}`);
  },
  
  closeAllExcept(id) {
    console.log(`Closing all players except: ${id}`);
    this.activePlayers.forEach((component, playerId) => {
      if (playerId !== id) {
        console.log(`Closing player: ${playerId}`);
        // Directly call close method on component instead of using events
        if (component && typeof component.close === 'function') {
          component.close();
        }
      }
    });
  }
};

const FileItem = ({ file, isFolder, onFolderClick, currentFolder, onPlay, isPlaying, isLoading: propIsLoading }) => {
  // Remove state for internal audio player
  const [isLoading, setIsLoading] = useState(false);

  const handleClick = () => {
    if (isFolder) {
      onFolderClick(file.name);
    }
  };

  const handlePlayClick = async (e) => {
    e.stopPropagation();
    
    // Use the onPlay prop from FileList.js
    if (onPlay) {
      onPlay(file);
    }
  };

  // Extract just the folder or file name for display
  // For folders in the current folder, show only the folder name
  const getDisplayName = () => {
    if (isFolder) {
      // For folders, display only the folder name without the full path
      if (currentFolder && file.name.startsWith(currentFolder)) {
        const relativePath = file.name.slice(currentFolder.length);
        // The folder name is the first part of the relative path
        return relativePath.split('/')[0] + '/';
      } else {
        // If not in a subfolder or at root, show the last part of the path
        const parts = file.name.split('/').filter(part => part.length > 0);
        return parts.length > 0 ? parts[parts.length - 1] + '/' : file.name;
      }
    } else {
      // For files, just show the filename without the path
      return file.name.split('/').pop();
    }
  };

  const displayName = getDisplayName();
  const iconClass = isFolder ? 'fas fa-folder' : getFileIcon(displayName);
  const iconColor = '#002c5f';
  const canPlayAudio = !isFolder && isAudioFile(displayName);
  const canPlayVideo = !isFolder && isVideoFile(displayName);
  const canPlay = canPlayAudio || canPlayVideo;
  const isEmptyFile = !isFolder && file.size === 0;
  
  // Use prop loading state if provided
  const effectiveIsLoading = propIsLoading !== undefined ? propIsLoading : isLoading;

  // Handle CSV file download
  const handleCsvDownload = async (e) => {
    e.stopPropagation();
    setIsLoading(true);
    
    try {
      const url = await apiService.getFileDownloadUrl(file.name);
      
      // Create and click a temporary download link
      const a = document.createElement('a');
      a.href = url;
      a.download = displayName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading CSV file:', error);
      alert('Failed to download file. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Determine file type label
  let typeLabel = 'File';
  if (isFolder) typeLabel = 'Folder';
  else if (isAudioFile(displayName)) typeLabel = 'Audio';
  else if (isVideoFile(displayName)) typeLabel = 'Video';
  else if (isCsvFile(displayName)) typeLabel = 'CSV';

  return (
    <tr>
      {/* Name column: icon + name (clickable for folders) */}
      <td>
        <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <i className={iconClass} style={{ fontSize: 20, color: iconColor }}></i>
          {isFolder ? (
            <span style={{ cursor: 'pointer', fontWeight: 500, color: '#001a36' }} onClick={handleClick}>{displayName}</span>
          ) : (
            <span style={{ fontWeight: 500 }}>{displayName}</span>
          )}
          {/* Remove internal audio player */}
        </span>
      </td>
      {/* Type column */}
      <td>{typeLabel}</td>
      {/* Size column */}
      <td>{!isFolder ? formatFileSize(file.size) : '-'}</td>
      {/* Actions column */}
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%' }}>
          {/* Play button for audio/video */}
          {!isFolder && (isAudioFile(displayName) || isVideoFile(displayName)) && file.size > 0 && (
            <button
              onClick={handlePlayClick}
              title={isAudioFile(displayName) ? 'Play Audio' : 'Play Video'}
              className="play-btn"
              style={{ 
                background: isPlaying ? '#e7f0ff' : '#f4f6fb', 
                border: 'none', 
                borderRadius: 6, 
                padding: 6, 
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              disabled={effectiveIsLoading}
            >
              <i 
                className={isPlaying ? "fas fa-pause" : "fas fa-play"} 
                style={{ color: '#001a36', fontSize: 16 }}
              ></i>
            </button>
          )}
          {/* Download button for files */}
          {!isFolder && (
            <button
              onClick={handleCsvDownload}
              title="Download"
              style={{ background: '#f4f6fb', border: 'none', borderRadius: 6, padding: 6, cursor: 'pointer' }}
              disabled={effectiveIsLoading}
            >
              <i className="fas fa-download" style={{ color: '#001a36', fontSize: 16 }}></i>
            </button>
          )}
        </div>
      </td>
    </tr>
  );
};

// Styled Components
const FileItemWrapper = styled.div`
  margin-bottom: 10px;
`;

const FileItemContainer = styled.div`
  display: flex;
  padding: 15px;
  border-radius: 8px;
  border: 1px solid var(--border-color);
  background: white;
  transition: var(--transition);
  position: relative;
  cursor: ${props => props.isFolder ? 'pointer' : 'default'};
  opacity: ${props => props.isLoading ? 0.7 : 1};
  pointer-events: ${props => props.isLoading ? 'none' : 'auto'};
  
  &:hover {
    box-shadow: var(--shadow);
    transform: translateY(-2px);
    border-color: var(--secondary-color);
  }
  
  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    height: 100%;
    width: 3px;
    background: ${props => props.isFolder ? 'var(--secondary-color)' : 'var(--primary-color)'};
    border-radius: 4px 0 0 4px;
    opacity: 0.5;
    transition: var(--transition);
  }
  
  &:hover::before {
    opacity: 1;
    width: 5px;
  }
`;

const FileInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
`;

const FileIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: var(--accent-color);
  border-radius: 8px;
  color: var(--primary-color);
  font-size: 18px;
  flex-shrink: 0;
  position: relative;
  
  ${props => props.isLoading && `
    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(255, 255, 255, 0.4);
      border-radius: 8px;
    }
  `}
`;

const FileInfoContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 5px;
`;

const FileMeta = styled.div`
  display: flex;
  gap: 15px;
  font-size: 12px;
  color: var(--text-light);
`;

const FileSize = styled.span``;

const FileDate = styled.span``;

const FileActions = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  align-items: center;
`;

const ActionButton = styled.button`
  background: var(--accent-color);
  border: none;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--primary-color);
  transition: var(--transition);
  opacity: ${props => props.disabled ? 0.5 : 1};
  
  &:hover:not(:disabled) {
    background: var(--primary-color);
    color: white;
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const AudioPlayerWrapper = styled.div`
  margin-top: 10px;
  max-height: ${props => props.show ? '1000px' : '0'};
  opacity: ${props => props.show ? '1' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease, opacity 0.3s ease;
`;

export default FileItem; 